import React from 'react';
import styled from 'styled-components';

import {
  promoSectionLightBg,
  referralsProcessSteps,
} from 'common/components/settings/referrals/process';
import FlexContainer from 'components/lib/ui/FlexContainer';
import Text from 'components/lib/ui/Text';
import ManualLink from 'components/lib/ui/link/ManualLink';
import { Promo } from 'components/settings/referrals/Promo';

import { fontWeight, radius, spacing, variables } from 'common/lib/theme/dynamic';

import { MONARCH_REFERRAL_TERMS_URL } from 'common/constants/externalUrls';

import type { ReferralConstantsFieldsFragment } from 'common/generated/graphql';

const Root = styled.div<{ isSidebarExpanded?: boolean }>`
  display: flex;
  gap: ${spacing.xxlarge};

  @media (max-width: ${({ isSidebarExpanded, theme }) =>
      isSidebarExpanded ? theme.breakPoints.lg : theme.breakPoints.sm}px) {
    flex-direction: column;
  }
`;

const Block = styled(FlexContainer)<{ isSidebarExpanded?: boolean }>`
  width: 50%;

  @media (max-width: ${({ isSidebarExpanded, theme }) =>
      isSidebarExpanded ? theme.breakPoints.lg : theme.breakPoints.sm}px) {
    width: 100%;
  }
`;

const ProcessBlock = styled(Block).attrs({ column: true })`
  gap: ${spacing.xxlarge};

  @media (max-width: ${({ isSidebarExpanded, theme }) =>
      isSidebarExpanded ? theme.breakPoints.lg : theme.breakPoints.sm}px) {
    order: 2;
  }
`;

const ProcessStep = styled.div`
  display: flex;
  gap: ${spacing.default};
`;

const ProcessNumberDot = styled(FlexContainer).attrs({ alignCenter: true, justifyCenter: true })`
  width: 32px;
  height: 32px;
  background: ${variables.color.background.secondary};
  border-radius: ${radius.xlarge};
`;

const ProcessItem = styled(FlexContainer).attrs({ column: true })`
  flex: 1;
  /* Make sure the referralUrl can break lines when the container gets small */
  overflow: hidden;
`;

const ReferralUrl = styled(Text).attrs({
  size: 'xsmall',
  color: 'content.secondary',
})`
  margin-top: ${spacing.xsmall};
  overflow-wrap: break-word;
`;

const ProcessDescription = styled(Text).attrs({
  size: 'small',
  color: 'content.secondary',
})`
  margin-top: ${spacing.xxsmall};
`;

const ProcessDisclaimer = styled(Text).attrs({
  size: 'xsmall',
  color: 'content.secondary',
})`
  margin-top: ${spacing.xlarge};
`;

const ProcessTermsLink = styled(ManualLink)`
  color: ${variables.color.content.primary};
  font-weight: ${fontWeight.book};

  &:hover {
    color: ${variables.color.content.secondary};
  }
`;

const PromosBlock = styled(Block).attrs({ justifyCenter: true })`
  border-radius: ${radius.medium};
  background: ${({ theme }) =>
    theme.uiTheme === 'light' ? promoSectionLightBg : theme.variables.color.background.secondary};
  padding: ${spacing.xxlarge};
  min-height: 322px;

  @media (max-width: ${({ isSidebarExpanded, theme }) =>
      isSidebarExpanded ? theme.breakPoints.lg : theme.breakPoints.sm}px) {
    order: 1;
  }
`;

const PromosContainer = styled(FlexContainer).attrs({ alignCenter: true })`
  flex: 1;
  position: relative;
  max-width: 350px;
`;

const PromoContainer = styled(FlexContainer).attrs({ alignCenter: true })`
  position: absolute;
  width: 100%;
  max-width: 56%;
`;

const PromoLeftContainer = styled(PromoContainer)`
  left: 0;
  transform: translateY(-1%);
`;

const PromoRightContainer = styled(PromoContainer)`
  right: 0;
  transform: translateY(1%);
`;

type Props = {
  className?: string;
  isSidebarExpanded?: boolean;
  referralUrl: string;
  constants: ReferralConstantsFieldsFragment;
};

export const ProcessSection = ({ constants, className, isSidebarExpanded, referralUrl }: Props) => (
  <Root className={className} isSidebarExpanded={isSidebarExpanded}>
    <ProcessBlock isSidebarExpanded={isSidebarExpanded}>
      {referralsProcessSteps.map((step, index) => (
        <ProcessStep key={index}>
          <ProcessNumberDot>
            <Text weight="medium" color="content.secondary" size="small">
              {index + 1}
            </Text>
          </ProcessNumberDot>

          <ProcessItem>
            <Text weight="medium" color="content.primary">
              {step.title}
            </Text>

            <ProcessDescription>{step.description}</ProcessDescription>

            {index === 0 && <ReferralUrl>{referralUrl}</ReferralUrl>}

            {step.descriptionLine2 && (
              <ProcessDescription>{step.descriptionLine2}</ProcessDescription>
            )}

            {step.disclaimer && (
              <ProcessDisclaimer>
                {step.disclaimer(constants.referralAnnualRewardLimitUsd)}{' '}
                <ProcessTermsLink
                  href={MONARCH_REFERRAL_TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  terms
                </ProcessTermsLink>
                .
              </ProcessDisclaimer>
            )}
          </ProcessItem>
        </ProcessStep>
      ))}
    </ProcessBlock>

    <PromosBlock isSidebarExpanded={isSidebarExpanded}>
      <PromosContainer>
        <PromoLeftContainer>
          <Promo variant="ticket" />
        </PromoLeftContainer>

        <PromoRightContainer>
          <Promo variant="giftCard" />
        </PromoRightContainer>
      </PromosContainer>
    </PromosBlock>
  </Root>
);
